import React, {FC} from 'react';
import styled from 'styled-components';

import {AsyncLoader} from '../../../../../../../../src/components/common/loaders/asyncLoader';
import {
  ContactDetailsContext,
  ContactDetailsVersionsEnum,
  defaultContactDetailsContact,
  SetMergeOverridesHandler,
  SetMergeOverridesOptions,
} from '../../../../../../../../src/components/contacts/contactDetailsContext';
import {
  renderContactDetailsLoadingSections,
  renderContactDetailsMergePreview,
} from '../../../../../../../../src/components/contacts/helpers/accordionSectionHelpers';
import {
  ConflictingMergeCardValues,
  handleSettingOverrideMergeValues,
} from '../../../../../../../../src/helpers/models/cardMergeHelpers';
import {CardModel} from '../../../../../../../../src/models/api/cardModel';
import {
  ContactDetailsSectionsEnum,
  defaultContactDetailSectionsExpandState,
} from '../../../../../../../../src/models/api/localPreferencesModel';
import {VisualContactModel} from '../../../../../../../../src/models/api/visualContactModel';
import {OverrideMergeValues} from '../../../../../../../../src/store/data/cards/cardsActions';
import {ContactsManagerMergeContactsState} from '../../../../../../../../src/store/panels/contacts/contactsManager/sidePanel/contactsManagerMergeContactsState';
import {Trans} from '../../../../../../../shared/helpers/src/internationalization/react';
import {AsyncHandler, HandlerOf} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../../../shared/helpers/src/types/asyncStatuses';
import {VisualSizesEnum} from '../../../../../../../shared/ui/src/styles/commonStyles';

/*
 * Props.
 */

interface MergeContactsSidePanelContentProps {
  contact?: VisualContactModel;
  sidePanelState?: ContactsManagerMergeContactsState;
  conflictingMergeValues?: ConflictingMergeCardValues;
  overrideMergeValues?: OverrideMergeValues;
  isMerging: boolean;
  setOverrideMergeValues: HandlerOf<OverrideMergeValues>;
  onRetryPreviewAsync: AsyncHandler;
}

interface RenderContactDetailsMergePreviewOptions {
  sidePanelState: ContactsManagerMergeContactsState;
  contact?: VisualContactModel;
  conflictingMergeValues?: ConflictingMergeCardValues;
  overrideMergeValues?: OverrideMergeValues;
  isMerging: boolean;
  onSetMergeOverrides: SetMergeOverridesHandler;
}

/*
 * Style.
 */

const StyledMergeContactsContentContainerDiv = styled.div`
  flex: 1;
  overflow: auto;
`;

const StyledErrorDiv = styled.div`
  padding: 64px 0;
`;

/*
 * Component.
 */

export const MergeContactsSidePanelContent: FC<MergeContactsSidePanelContentProps> = (props) => {
  const {
    contact,
    sidePanelState,
    conflictingMergeValues,
    overrideMergeValues,
    isMerging,
    onRetryPreviewAsync,
    setOverrideMergeValues,
  } = props;

  if (!sidePanelState) {
    return null;
  }

  const onSetMergeOverrides: SetMergeOverridesHandler = (options: SetMergeOverridesOptions) => {
    handleSettingOverrideMergeValues(overrideMergeValues, options, setOverrideMergeValues);
  };

  return (
    <StyledMergeContactsContentContainerDiv>
      {maybeRenderErrorState(sidePanelState, onRetryPreviewAsync)}
      {maybeRenderContactDetailsMergePreview({
        sidePanelState,
        contact,
        conflictingMergeValues,
        overrideMergeValues,
        isMerging,
        onSetMergeOverrides,
      })}
    </StyledMergeContactsContentContainerDiv>
  );
};

/*
 * Helpers.
 */

function maybeRenderErrorState(
  sidePanelState: ContactsManagerMergeContactsState,
  retryPreviewAsync: AsyncHandler,
) {
  if (sidePanelState.status !== AsyncStatusesEnum.FAILED) {
    return null;
  }
  return (
    <StyledErrorDiv>
      <AsyncLoader
        size={VisualSizesEnum.LARGE}
        status={AsyncStatusesEnum.FAILED}
        error={<Trans id="contacts-manager-merge-side-panel-error-message">Something went wrong.</Trans>}
        onRetryClick={retryPreviewAsync}
      />
    </StyledErrorDiv>
  );
}

function maybeRenderContactDetailsMergePreview(options: RenderContactDetailsMergePreviewOptions) {
  const {
    sidePanelState,
    conflictingMergeValues,
    contact,
    overrideMergeValues,
    isMerging,
    onSetMergeOverrides,
  } = options;

  if (sidePanelState.status === AsyncStatusesEnum.FAILED) {
    return null;
  }
  if (
    sidePanelState.status === AsyncStatusesEnum.LOADING ||
    !conflictingMergeValues ||
    !sidePanelState.previewCardMerge ||
    !contact
  ) {
    return renderContactDetailsLoadingSections(isMerging, {
      ...defaultContactDetailSectionsExpandState,
      [ContactDetailsSectionsEnum.SUMMARY]: false,
      [ContactDetailsSectionsEnum.CONVERSATIONS]: false,
    });
  }

  return (
    <ContactDetailsContext.Provider
      value={buildContactDetailsContextValue(
        sidePanelState.previewCardMerge.card,
        conflictingMergeValues,
        overrideMergeValues,
        isMerging,
        onSetMergeOverrides,
      )}
    >
      {renderContactDetailsMergePreview({
        panelId: defaultContactDetailsContact.panelId,
        previewCardMerge: sidePanelState.previewCardMerge,
        contact,
      })}
    </ContactDetailsContext.Provider>
  );
}

function buildContactDetailsContextValue(
  card: CardModel,
  conflictingMergeValues: ConflictingMergeCardValues,
  overrideMergeValues: OverrideMergeValues | undefined,
  isMerging: boolean,
  onSetMergeOverrides: SetMergeOverridesHandler,
) {
  return {
    ...defaultContactDetailsContact,
    version: ContactDetailsVersionsEnum.VERSION_2,
    isReadOnly: true,
    mergeContext: {
      ...defaultContactDetailsContact.mergeContext,
      isMerging,
      conflictingMergeValues,
      overrideMergeValues,
      setMergeOverrides: onSetMergeOverrides,
    },
    storedActionsContext: {
      ...defaultContactDetailsContact.storedActionsContext,
      unsavedCard: card,
    },
  };
}
